import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useCurrency } from './CurrencyContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { userDoc, updateUser } = useAuth();
  const { currency, getProductPrice } = useCurrency();
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const lastPersistedRef = useRef('');

  const minifyCartItem = (item) => {
    const images = Array.isArray(item?.images) ? item.images : [];
    const safeImages = images
      .map((src) => (typeof src === 'string' ? src : ''))
      .filter(Boolean)
      .filter((src) => !src.startsWith('data:'))
      .slice(0, 1);

    return {
      id: item?.id,
      name: item?.name,
      price: item?.price,
      prices: item?.prices,
      images: safeImages,
      size: item?.size,
      color: item?.color,
      quantity: item?.quantity,
    };
  };

  const remoteCart = useMemo(() => {
    const items = Array.isArray(userDoc?.cart) ? userDoc.cart : [];
    return items.map(minifyCartItem);
  }, [userDoc?.cart]);

  useEffect(() => {
    setCart(remoteCart);
  }, [remoteCart]);

  const persistCart = async (nextCart) => {
    if (typeof updateUser !== 'function') return;
    const minimized = (Array.isArray(nextCart) ? nextCart : []).map(minifyCartItem);
    const serialized = JSON.stringify(minimized);
    if (lastPersistedRef.current === serialized) return;
    lastPersistedRef.current = serialized;
    await updateUser({ cart: minimized });
  };

  const addToCart = (product, size, color, quantity = 1) => {
    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(
        item => item.id === product.id && item.size === size && item.color === color
      );

      if (existingItemIndex > -1) {
        const newCart = [...prevCart];
        newCart[existingItemIndex].quantity += quantity;
        persistCart(newCart);
        return newCart;
      }

      const next = [...prevCart, { ...product, size, color, quantity }];
      persistCart(next);
      return next;
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCart(prevCart => {
      const next = prevCart.filter(
        item => !(item.id === productId && item.size === size && item.color === color)
      );
      persistCart(next);
      return next;
    });
  };

  const updateQuantity = (productId, size, color, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, size, color);
      return;
    }

    setCart(prevCart => {
      const next = prevCart.map(item =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity }
          : item
      );
      persistCart(next);
      return next;
    });
  };

  const clearCart = () => {
    setCart([]);
    persistCart([]);
  };

  const getCartTotal = () => {
    let total = 0;
    for (const item of cart) {
      const unit = typeof getProductPrice === 'function' ? getProductPrice(item, currency) : Number(item?.price);
      if (!Number.isFinite(Number(unit))) return null;
      const qty = Number(item?.quantity || 0);
      total += Number(unit) * qty;
    }
    return total;
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        isCartOpen,
        setIsCartOpen,
        toggleCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};